import React from 'react';
import { prettyKey } from '../hooks/useEinsteinChat';
import { SeeFullPageLink } from './EinsteinLinks';
import { PANEL_REGISTRY, ComposePanelGroup } from './EinsteinComposePanels';

// ── Response-shape rendering (Home redesign Task 3) ─────────────────────────────
// Each of the router's four real response shapes gets its own compact renderer — nothing here
// is LLM-generated prose; every value shown is read directly off the JSON the router returned.
// Shared by the compact Home console (EinsteinConsole.js) and the dedicated Einstein tab
// (pages/EinsteinChat.js) — extracted 2026-09-03 so both stay in sync with the router's actual
// contract instead of two renderers drifting apart.

function QuickStatBody({ items, textColor, mutedColor, goToRoute, accent }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((item, i) => {
        if (item.tool === 'getPlayerAvailability') {
          const r = item.result;
          if (r?.status !== 'resolved') {
            return <div key={i} style={{ color: mutedColor }}>No availability data for {item.player?.name || 'that player'}.</div>;
          }
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div>
                <strong style={{ color: textColor }}>{item.player.name}</strong> — {r.stageLabel}
                {r.daysInStage != null ? ` (${r.daysInStage}d)` : ''}
              </div>
              <SeeFullPageLink sourceRoute={item.sourceRoute} sourceKey={item.sourceKey} goToRoute={goToRoute} accent={accent} />
            </div>
          );
        }
        if (item.tool === 'getSessionMetrics') {
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div><strong style={{ color: textColor }}>{item.player?.name}</strong></div>
              {(item.sessions || []).map((s, j) => (
                s.status === 'resolved' ? (
                  <div key={j} style={{ fontSize: 13.5, color: mutedColor }}>
                    {s.session?.title} ({s.session?.date}) — TD {s.metrics.td}m · RD {s.metrics.rd}m · HID {s.metrics.hid}m · SA {s.metrics.sa} · HMLD {s.metrics.hmld}m
                    {s.isEstimated ? ' · estimated' : ''}
                  </div>
                ) : (
                  <div key={j} style={{ fontSize: 13.5, color: mutedColor }}>No metrics recorded for that session.</div>
                )
              ))}
              <SeeFullPageLink sourceRoute={item.sourceRoute} sourceKey={item.sourceKey} goToRoute={goToRoute} accent={accent} />
            </div>
          );
        }
        if (item.tool === 'getLatestPhysicalTest') {
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div><strong style={{ color: textColor }}>{item.player?.name}</strong> — {prettyKey(item.testType)}</div>
              {(item.results || []).map((r, j) => (
                <div key={j} style={{ fontSize: 13.5, color: mutedColor }}>
                  {r.date} — {Object.entries(r.values).map(([k, v]) => `${prettyKey(k)}: ${v}`).join(' · ')}
                </div>
              ))}
              <SeeFullPageLink sourceRoute={item.sourceRoute} sourceKey={item.sourceKey} goToRoute={goToRoute} accent={accent} />
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

// metric_comparison (Phase 3, in progress) — every number here is read straight off the router's
// response (msg.data.results[].sum/results/msg.data.delta), computed deterministically in
// services/comparisons.js. These components format and sign numbers for display; they never
// compute anything themselves. Two metrics, two shapes — gps_load sums to a number + delta;
// physical_testing is deliberately a LIST (see comparisons.js's getPlayerTestResults header
// comment for why averaging a discrete test result would misrepresent it), so it gets its own
// renderer rather than forcing both into one shape.
function GpsLoadComparisonBody({ data, goToRoute, accent, textColor, mutedColor }) {
  const { results, delta, window, sessionType, sourceKey } = data;
  const windowLabel = window?.mode === 'days'
    ? `last ${window.n} day${window.n === 1 ? '' : 's'}`
    : `last ${window?.n} ${sessionType} session${window?.n === 1 ? '' : 's'}`;
  const signed = v => (v > 0 ? `+${v}` : `${v}`);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 12.5, color: mutedColor }}>Summed over their {windowLabel}:</div>
      {results.map((r, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div>
            <strong style={{ color: textColor }}>{r.player.name}</strong> — TD {r.sum.td}m · RD {r.sum.rd}m · HID {r.sum.hid}m · SA {r.sum.sa} · HMLD {r.sum.hmld}m
            {r.sessionsUsed < r.sessionsRequested ? ` (only ${r.sessionsUsed}/${r.sessionsRequested} sessions had data recorded)` : ''}
            {r.anyEstimated ? ' · includes an estimated session' : ''}
          </div>
          <SeeFullPageLink sourceRoute={r.sourceRoute} sourceKey={sourceKey} goToRoute={goToRoute} accent={accent} />
        </div>
      ))}
      {delta && results.length === 2 && (
        <div style={{ fontSize: 13.5, color: mutedColor }}>
          Difference ({results[0].player.name} − {results[1].player.name}): TD {signed(delta.td)}m · RD {signed(delta.rd)}m · HID {signed(delta.hid)}m · SA {signed(delta.sa)} · HMLD {signed(delta.hmld)}m
        </div>
      )}
    </div>
  );
}

// physical_testing — a plain list of each player's raw results, most recent first, no
// averaging/summing anywhere (a per-test-type decision, not a rendering shortcut). Column names
// vary by test type (test_sprint has sprint_5m/10m/30m; test_cmj has height_cm/peak_power_wkg;
// ...) so values are listed generically rather than a hardcoded layout per type.
function PhysicalTestComparisonBody({ data, goToRoute, accent, textColor, mutedColor }) {
  const { results, testType, window, sourceKey } = data;
  const windowLabel = window?.mode === 'days'
    ? `last ${window.n} day${window.n === 1 ? '' : 's'}`
    : `last ${window?.n} result${window?.n === 1 ? '' : 's'}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 12.5, color: mutedColor }}>{prettyKey(testType)} — {windowLabel}, most recent first:</div>
      {results.map((r, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div><strong style={{ color: textColor }}>{r.player.name}</strong></div>
          {r.results.length ? r.results.map((test, j) => (
            <div key={j} style={{ fontSize: 13.5, color: mutedColor }}>
              {test.date} — {Object.entries(test.values).map(([k, v]) => `${prettyKey(k)}: ${v}`).join(' · ')}
            </div>
          )) : <div style={{ fontSize: 13.5, color: mutedColor }}>No results in that window.</div>}
          <SeeFullPageLink sourceRoute={r.sourceRoute} sourceKey={sourceKey} goToRoute={goToRoute} accent={accent} />
        </div>
      ))}
    </div>
  );
}

// ACWR zone colors — matches src/pages/gps/EWMA.js's own riskInfo() thresholds/colors exactly
// (not pillarScores.js's differently-named safe/caution/red_flag zones — see
// services/comparisons.js's getPlayerACWR header comment for why this deliberately follows
// EWMA.js, the live page these results link to).
const ACWR_ZONE_COLOR = { high_risk: '#ef4444', caution: '#f59e0b', optimal: '#10b981', underload: '#94a3b8' };
const ACWR_ZONE_LABEL = { high_risk: 'High Risk', caution: 'Caution', optimal: 'Optimal', underload: 'Underload' };

// acwr (2026-09-10) — a LIST of each player's ACWR readings, most recent first, never averaged
// (same "a ratio at one point in time isn't additive" reasoning as physical_testing's list shape).
function AcwrComparisonBody({ data, goToRoute, accent, textColor, mutedColor }) {
  const { results, window, sourceKey } = data;
  const windowLabel = window?.mode === 'days'
    ? `last ${window.n} day${window.n === 1 ? '' : 's'}`
    : `last ${window?.n} reading${window?.n === 1 ? '' : 's'}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 12.5, color: mutedColor }}>ACWR — {windowLabel}, most recent first:</div>
      {results.map((r, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div><strong style={{ color: textColor }}>{r.player.name}</strong></div>
          {r.readings.length ? r.readings.map((reading, j) => (
            <div key={j} style={{ fontSize: 13.5, color: mutedColor, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: ACWR_ZONE_COLOR[reading.zone] || mutedColor, flex: 'none' }} />
              {reading.date} — {reading.acwr != null ? reading.acwr.toFixed(2) : '—'}
              {reading.zone && <span style={{ color: ACWR_ZONE_COLOR[reading.zone] }}>({ACWR_ZONE_LABEL[reading.zone]})</span>}
              {reading.chronicHighlyImputed ? ' · chronic window >30% estimated' : ''}
            </div>
          )) : <div style={{ fontSize: 13.5, color: mutedColor }}>No readings in that window.</div>}
          <SeeFullPageLink sourceRoute={r.sourceRoute} sourceKey={sourceKey} goToRoute={goToRoute} accent={accent} />
        </div>
      ))}
    </div>
  );
}

function MetricComparisonBody({ data, goToRoute, accent, textColor, mutedColor }) {
  if (data.metric === 'physical_testing') {
    return <PhysicalTestComparisonBody data={data} goToRoute={goToRoute} accent={accent} textColor={textColor} mutedColor={mutedColor} />;
  }
  if (data.metric === 'acwr') {
    return <AcwrComparisonBody data={data} goToRoute={goToRoute} accent={accent} textColor={textColor} mutedColor={mutedColor} />;
  }
  return <GpsLoadComparisonBody data={data} goToRoute={goToRoute} accent={accent} textColor={textColor} mutedColor={mutedColor} />;
}

function RouteChip({ registryKey, route, goToRoute, accent, textColor }) {
  return (
    <button type="button" onClick={() => goToRoute(route)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '8px 14px',
        borderRadius: 999, border: `1px solid ${accent}`, background: 'transparent', color: textColor,
        fontSize: 13, fontWeight: 600, cursor: 'pointer',
      }}>
      Open {prettyKey(registryKey)} →
    </button>
  );
}

export default function EinsteinMessage({ msg, goToRoute, accent, textColor, mutedColor, onOptionClick }) {
  if (msg.kind === 'text') return <span>{msg.text}</span>;

  if (msg.kind === 'needs_clarification') {
    return (
      <div>
        <div>{msg.data.question}</div>
        {!!(msg.data.options || []).length && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {msg.data.options.map((opt, i) => (
              <button key={i} type="button" onClick={() => onOptionClick(opt, msg.data.question)}
                style={{ padding: '7px 14px', borderRadius: 999, border: `1px solid ${accent}`, background: 'transparent', color: textColor, fontSize: 13, cursor: 'pointer' }}>
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (msg.kind === 'quick_stat') {
    return <QuickStatBody items={msg.data.items} textColor={textColor} mutedColor={mutedColor} goToRoute={goToRoute} accent={accent} />;
  }

  if (msg.kind === 'metric_comparison') {
    return <MetricComparisonBody data={msg.data} goToRoute={goToRoute} accent={accent} textColor={textColor} mutedColor={mutedColor} />;
  }

  if (msg.kind === 'route_to_page') {
    return (
      <div>
        <div>Here's the page for that.</div>
        <RouteChip registryKey={msg.data.registryKey} route={msg.data.route} goToRoute={goToRoute} accent={accent} textColor={textColor} />
      </div>
    );
  }

  if (msg.kind === 'compose') {
    // Elements sharing a registryKey that has a real panel component (PANEL_REGISTRY, 2026-09-10)
    // render as actual side-by-side panels; everything else still falls back to a route chip —
    // see EinsteinComposePanels.js's header comment for which keys that covers today.
    const order = [];
    const groups = new Map();
    for (const el of msg.data.elements) {
      if (!groups.has(el.registryKey)) { groups.set(el.registryKey, []); order.push(el.registryKey); }
      groups.get(el.registryKey).push(el);
    }
    return (
      <div>
        {msg.data.note && <div style={{ color: mutedColor, marginBottom: 6 }}>{msg.data.note}</div>}
        <div style={{ marginBottom: 8 }}>{msg.data.composeSupported ? 'Here they are side by side:' : "Here's what's available:"}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {order.map(registryKey => {
            const elements = groups.get(registryKey);
            if (PANEL_REGISTRY[registryKey]) {
              return <ComposePanelGroup key={registryKey} registryKey={registryKey} elements={elements} prettyKey={prettyKey} goToRoute={goToRoute} accent={accent} />;
            }
            return (
              <div key={registryKey} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {elements.map((el, i) => (
                  <RouteChip key={i} registryKey={el.registryKey} route={el.route} goToRoute={goToRoute} accent={accent} textColor={textColor} />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (msg.kind === 'error') {
    return <span style={{ color: '#f87171' }}>{msg.text}</span>;
  }

  return null;
}
